/**
 * Finance vertical — every user-visible string in the sprint that NAMES finance.
 *
 * Two sources feed this file, and the split is deliberate:
 *
 *   1. Strings that already live in `finance-discovery/_data/*` (M3_INTRO, READING_PRIMER,
 *      ACTIVITY_COPY, M4_INTRO, M4_SELECTION, M4_ACTIVITY, M1_RESULTS_COPY) are REFERENCED, never
 *      retyped. The finance screens are live in production reading those same constants, so a copy
 *      edit there must reach the engine automatically — a transcribed duplicate would silently drift.
 *   2. Strings the shipped screens hardcode in JSX (the welcome hero + step cards, m1-intro's lead,
 *      m2-intro's three points, the `*-complete` sub-lines, m4-activity's panel label and CTA, the
 *      exit screen's archetype line) have no `_data` home, so they are transcribed here verbatim —
 *      character for character, apostrophes included. This file is now their source of truth.
 *
 * Placeholders (`{count}`, `{archetype}`, `{subject}`) replace what the JSX interpolated inline. The
 * contract in `../_engine/types.ts` documents exactly which strings carry which — do not add more.
 */

import { M1_RESULTS_COPY } from '../../../finance-discovery/_data/m1-cards';
import { ACTIVITY_COPY, M3_INTRO, READING_PRIMER } from '../../../finance-discovery/_data/m3-numbers';
import { M4_ACTIVITY, M4_INTRO, M4_SELECTION } from '../../../finance-discovery/_data/m4-read';
import type { LabsCopy } from '../_engine/types';

/**
 * The arithmetic of an income statement, by primer row: Revenue, − COGS, = Gross Profit, − Opex,
 * = Operating Income. The shipped screen holds these in a positional array PARALLEL to the copy rows
 * (`OPERATORS` in m3-reading.tsx), which silently breaks if a row is ever inserted. Folding them into
 * the rows themselves makes the pairing structural.
 */
const PRIMER_OPERATORS: readonly string[] = ['', '−', '=', '−', '='];

export const FINANCE_COPY: LabsCopy = {
  m1: {
    activity: {
      eyebrow: 'Module 1 · A Day in the Life',
      helper: 'Eight activities from a real finance day',
    },
    complete: {
      sub: "Nice work. Keep going, you've got this.",
    },
    intro: {
      accentEm: 'would I want to do this every day?',
      accentPre: "You'll see {count} real activities, one at a time. For each one, ask yourself:",
      headline: 'A day in the life',
      lead: 'Finance careers split into two worlds. Investing analysts decide which companies to buy. Corporate-finance analysts run the numbers inside one company. The day-to-day work looks very different.',
    },
    results: {
      bridge: M1_RESULTS_COPY.bridge,
      // Finance names its lean buckets after its own poles (`strongFpa` / `strongInvest`); the engine
      // names them after the axis (`strongA` / `strongB`) so `computeLean` never learns a domain word.
      // Fpa → A (inside the business), Invest → B (outside it).
      /**
       * Shown INSTEAD of the split reading when the learner swiped right on nothing.
       *
       * Without it, `computeLean` has no signal, returns a dead-even 50/50, and the split copy tells
       * someone who rejected all eight days that "both halves pulled you on different days" above two
       * bars reading 0 and 0. Found by a design review on 2026-08-26.
       */
      noneChosen:
        'You passed on all eight. That is real information, not a failed quiz: those are the days, and none of them appealed. Finance may not be your thing, and the useful question now is which kind of work is.',
      interpretation: {
        moderateA: M1_RESULTS_COPY.interpretation.moderateFpa,
        moderateB: M1_RESULTS_COPY.interpretation.moderateInvest,
        split: M1_RESULTS_COPY.interpretation.split,
        strongA: M1_RESULTS_COPY.interpretation.strongFpa,
        strongB: M1_RESULTS_COPY.interpretation.strongInvest,
      },
    },
  },
  m2: {
    complete: {
      sub: "Halfway there. You're reading people and numbers now!",
    },
    intro: {
      headline: 'Your mindset, in 4 situations',
      lead: "What you'd do is one signal. How you think is the other.",
      points: [
        '4 real situations a finance person faces',
        'Two reasonable reads on each. Pick the one that fits you',
        'No right answers, only honest ones',
      ],
    },
    results: {
      footer: 'This will appear on your analysis at the end.',
    },
  },
  m3: {
    activity: {
      diagnosisHeading: ACTIVITY_COPY.diagnosisHeading,
      diagnosisPlaceholder: ACTIVITY_COPY.diagnosisPlaceholder,
      diagnosisSample: ACTIVITY_COPY.diagnosisSample,
      eyebrow: ACTIVITY_COPY.modulePill,
      feedbackCorrect: ACTIVITY_COPY.feedbackNailedIt,
      feedbackWrong: ACTIVITY_COPY.feedbackTryAnother,
      metricsHeading: ACTIVITY_COPY.ratiosHeading,
      metricsHelper: ACTIVITY_COPY.ratiosHelper,
      revealBody: ACTIVITY_COPY.revealBody,
      revealLabel: ACTIVITY_COPY.revealLabel,
      subhead: ACTIVITY_COPY.subhead,
      tableTag: ACTIVITY_COPY.tableTag,
    },
    complete: {
      sub: 'One more module, then your real analysis.',
    },
    intro: {
      chips: M3_INTRO.chips,
      headline: M3_INTRO.headline,
      subtitle: M3_INTRO.subtitle,
    },
    primer: {
      // Load-bearing shape: the engine italicises everything after the first ": ", so the thesis must
      // keep its colon ("...answers one question: did this business make money...").
      body: READING_PRIMER.body,
      eyebrow: READING_PRIMER.modulePill,
      headline: READING_PRIMER.headline,
      pullQuote: READING_PRIMER.pullQuote,
      rows: READING_PRIMER.rows.map((row, i) => ({
        hint: row.hint,
        label: row.label,
        operator: PRIMER_OPERATORS[i] ?? '',
      })),
    },
  },
  // The pre-paywall hand-off. Body copy is deliberately near-identical across verticals: what changes
  // between tracks is the artifact the learner just made, not what a six-week externship is.
  handoff: {
    artifactTitle: 'Company valuation',
    body: 'That was {duration} of the real thing. The full externship runs six weeks with a real company and a certificate at the end. Your free trial starts it.',
    duration: '15 minutes',
    headline: 'You just finished your first Finance project',
    trackLabel: 'Finance track',
  },
  // Verbatim from the shipped offer modal + card. It provokes where `welcome` describes — that framing
  // is the offer's whole job, and it is the copy the live experiment's offer→start rate was measured on.
  offer: {
    body: '15 minutes, start to finish: learn to read the numbers, then analyze a real public company & walk away with work worth sharing',
    eyebrow: 'Finance Track',
    headlineLead: 'Stop wondering if finance is for you.',
    headlineTail: 'Spend 15 minutes finding out.',
    tilePromise: "Break down a real public company's numbers",
  },
  prepaywallOffer: {
    body: 'You told us Finance is your track. This 15-minute exercise is the actual work: read the numbers, analyze a real company, keep what you make.',
    eyebrow: 'Finance Track',
    headlineLead: 'Try the real work, free,',
    headlineTail: 'before you pay.',
  },
  m4: {
    activity: {
      feedbackWrong: M4_ACTIVITY.feedbackTryAnother,
      nameHeading: M4_ACTIVITY.nameHeading,
      pickHeading: M4_ACTIVITY.pickHeading,
      pickHelper: M4_ACTIVITY.pickHelper,
      sampleFooter: M4_ACTIVITY.sampleFooter,
      sampleHeading: M4_ACTIVITY.sampleHeading,
      sampleHelper: M4_ACTIVITY.sampleHelper,
      samplePanelLabel: 'Sample analysis · curated example',
      sampleRunCta: M4_ACTIVITY.sampleTag,
      // The shipped screen hardcodes this label and leaves `M4_ACTIVITY.submitCta` ("Submit") unused.
      // The rendered string is the one that ships, so it is the one the pack carries.
      submitCta: 'See my analysis',
      takeHeading: M4_ACTIVITY.takeHeading,
      takeHelper: M4_ACTIVITY.takeHelper,
      takePlaceholder: M4_ACTIVITY.takePlaceholder,
    },
    intro: {
      body: M4_INTRO.body,
      headline: M4_INTRO.headline,
    },
    selection: {
      eyebrow: M4_SELECTION.modulePill,
      headline: M4_SELECTION.headline,
      subhead: M4_SELECTION.subhead,
    },
  },
  recommendations: {
    archLineNoSubject: "You're {archetype}. Here's where this goes next.",
    archLineWithSubject: "You're {archetype}. Here's where your {subject} analysis goes next.",
  },
  welcome: {
    cta: 'Begin',
    // The pill's trailing "· Discovery experience" is invariant chrome (every vertical is one), so it
    // stays in the engine; only the track name is finance's to supply.
    eyebrow: 'Finance Track',
    headlineLead: 'What does a finance career actually look like?',
    headlineTail: 'Spend 15 minutes finding out.',
    lead: 'Two real career paths. Your first financial analysis.',
    metaDuration: 'About 15 minutes',
    steps: [
      { sub: 'See how two finance paths spend a day', title: 'Meet two analysts' },
      { sub: 'Quick reads reveal how you think', title: 'Discover your mindset' },
      { sub: 'Read the numbers behind a name you know', title: 'Analyze a real company' },
    ],
  },
};
